/**
 * Map a file path to which Live Preview slot it should update (html/css/js).
 * Used so nested paths (e.g. src/index.html) still refresh the preview.
 */
export function webPreviewSlotForPath(rawPath) {
  const p = String(rawPath || "")
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")
    .trim()
    .toLowerCase();
  if (!p) return null;
  if (p.endsWith(".html") || p.endsWith(".htm")) return "html";
  if (p.endsWith(".css")) return "css";
  if (p.endsWith(".js") && !p.endsWith(".json")) return "js";
  return null;
}

/**
 * True when the HTML string should be loaded as a full document in the preview iframe
 * (not injected as a fragment inside our wrapper template — which breaks real pages).
 */
export function looksLikeFullHtmlDocument(raw) {
  const s = String(raw || "").trim();
  if (s.length < 14) return false;
  const lower = s.toLowerCase();
  if (lower.startsWith("<!doctype")) return true;
  // Optional XML declaration before DOCTYPE
  if (/^<\?xml\s/i.test(s) && (lower.includes("<!doctype") || /<\s*html\b/i.test(s))) return true;
  if (/<\s*html\b/i.test(s)) return true;
  if (/<\s*head\b/i.test(s) && /<\s*body\b/i.test(s)) return true;
  return false;
}

/** Basenames that map to a different DOM section id (starter marketing shell). */
const PREVIEW_FILE_BASENAME_TO_SECTION_ID = {
  "auth-integration": "auth",
  "stripe-integration": "pricing",
};

/** Bundled / infra scripts — no matching #id section in typical marketing pages. */
const PREVIEW_SCROLL_SKIP_BASENAMES = new Set(["app", "config", "api", "styles"]);

/**
 * If opening this path in the IDE should scroll Live Preview to a section, return `#section-id`.
 * Matches `frontend/product.js`, `product.css`, etc. (kebab-case basename before extension).
 */
/** Remap greenfield root filenames into the IDE starter layout (frontend/*). */
const IDE_STARTER_BARE_TO_CANONICAL = {
  "index.html": "frontend/index.html",
  "styles.css": "frontend/styles.css",
  "app.js": "frontend/app.js",
};

/**
 * When the kit includes frontend/index.html, merge bare index.html / styles.css / app.js
 * into frontend/* so AI patches don't fork a parallel site at repo root.
 */
export function remapPatchFilesForIdeStarterKit(files) {
  const src = files && typeof files === "object" ? files : null;
  if (!src) return files;
  const out = { ...src };
  for (const [bare, canonical] of Object.entries(IDE_STARTER_BARE_TO_CANONICAL)) {
    if (!Object.prototype.hasOwnProperty.call(out, bare)) continue;
    const c = out[bare];
    delete out[bare];
    if (!Object.prototype.hasOwnProperty.call(out, canonical)) {
      out[canonical] = c;
    }
  }
  return out;
}

const STARTER_ROOT_MARKERS = ["zolaid-site-root", 'id="zolaid-site-root"', "id='zolaid-site-root'"];

/** True when `previous` is the Zolaid marketing starter shell (fragment with known root id). */
export function isIdeStarterMarketingIndexFragment(previous) {
  const s = String(previous || "");
  if (s.length < 80) return false;
  return STARTER_ROOT_MARKERS.some((m) => s.includes(m));
}

/**
 * AI often returns a tiny single-file brochure (`<!DOCTYPE html>…`) which breaks the bundled preview
 * and drops Stripe/auth/sections. Block auto-applying that over the real starter.
 */
export function wouldDestroyIdeStarterIndex(previous, next) {
  const prev = String(previous || "");
  const n = String(next || "").trim();
  if (!isIdeStarterMarketingIndexFragment(prev)) return false;
  if (!n) return true;
  if (looksLikeFullHtmlDocument(n)) return true;
  if (!STARTER_ROOT_MARKERS.some((m) => n.includes(m))) return true;
  return false;
}

/**
 * Remove bad `frontend/index.html` from an AI patch; keep other files. Returns human-readable warning.
 */
export function filterIdeStarterIndexRegression(files, generatedFiles) {
  const f = files && typeof files === "object" ? { ...files } : null;
  if (!f) return { files, skippedIndex: false, warning: "" };
  const gen = generatedFiles && typeof generatedFiles === "object" ? generatedFiles : {};
  const path = "frontend/index.html";
  if (!Object.prototype.hasOwnProperty.call(f, path)) return { files: f, skippedIndex: false, warning: "" };
  const prev = String(gen[path] ?? "");
  const next = String(f[path] ?? "");
  if (!wouldDestroyIdeStarterIndex(prev, next)) return { files: f, skippedIndex: false, warning: "" };
  delete f[path];
  const warning =
    "Did not apply AI changes to frontend/index.html: they would replace the Zolaid starter with a minimal or full-page HTML document. This site must stay as a fragment with <div id=\"zolaid-site-root\"> and <section id=\"…\"> blocks (pricing/auth/contact/Stripe). Refurbish copy and styles inside that structure, or edit frontend/styles.css and section *.js files. Use the Zolaid IDE assistant (not external VS Code chat) so it receives the full starter context.";
  return { files: f, skippedIndex: true, warning };
}

export function previewScrollHashForPath(rawPath) {
  const p = String(rawPath || "")
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")
    .trim()
    .toLowerCase();
  if (!p) return null;
  const m = p.match(/(?:^|\/)([a-z0-9-]+)\.(js|css)$/i);
  if (!m) return null;
  const base = m[1];
  if (PREVIEW_SCROLL_SKIP_BASENAMES.has(base)) return null;
  const id = PREVIEW_FILE_BASENAME_TO_SECTION_ID[base] || base;
  return `#${id}`;
}
